<template>
    <a-card :title="$t('screenTitle.billingPcLoggedInGameManage')">
        <a-collapse class="mb-20">
            <a-collapse-panel header="Tìm PC qua tên tài khoản">
                <a-form ref="formType" :model="formModel" @finish="onFinish" :scrollToFirstError="true"
                    layout="vertical">
                    <a-form-item :label="$t('labelText.user_name')" name="user_name">
                        <a-input v-model:value="formModel.user_name" />
                    </a-form-item>

                    <a-form-item>
                        <a-button type="primary" html-type="submit" :loading="isLoading">{{ $t('text.search') }}
                        </a-button>
                    </a-form-item>
                </a-form>
            </a-collapse-panel>
        </a-collapse>

        <a-table :dataSource="dataSource" :columns="columns" :pagination="pagination" @change="onChange"
            :loading="isLoading" :row-key="record => record.id" v-model:expanded-row-keys="expandedRowKeys" :scroll="{x: '100%'}">
            <template #bodyCell="{ column, record, text }">
                <template v-if="column.dataIndex === 'check_type'">
                    <a-select :value="text" style="width: 100%;" :options="optionsCheckType" @change="(v: any) => onChangeCheckType(record, v)" />
                </template>
                <template v-else-if="column.dataIndex === 'pc'">
                    <span>{{ text }}</span>
                    <a-tag v-if="record.bapls?.filter((elm:any) => elm.account && (elm.account.name === formModel.user_name) && (elm.account.pc_online === record.id))?.length" color="red">Online PC này</a-tag>
                </template>
            </template>

            <template #customFilterDropdown="{ setSelectedKeys, selectedKeys, confirm, clearFilters, column }">
                <div style="padding: 8px">
                    <a-input ref="searchInput" :placeholder="`Search ${column.dataIndex}`" :value="selectedKeys"
                        style="width: 188px; margin-bottom: 8px; display: block"
                        @change="(e: any) => setSelectedKeys(e.target.value)"
                        @pressEnter="handleSearch(selectedKeys, confirm, column.dataIndex)" />
                    <a-button type="primary" size="small" style="width: calc(60% - 8px); margin-right: 8px"
                        @click="handleSearch(selectedKeys, confirm, column.dataIndex)">
                        <template #icon>
                            <SearchOutlined />
                        </template>
                        <span>{{ $t('text.search') }}</span>
                    </a-button>
                    <a-button size="small" style="width: 40%" @click="handleReset(clearFilters)">Reset</a-button>
                </div>
            </template>

            <template #expandedRowRender="{ record }">
                <a-table :dataSource="record.bapls" :columns="columnsBAPLs">
                    <template #bodyCell="{ column, record: bapl_record, text }">
                        <template v-if="column.dataIndex === 'pc_id'">
                            <a-tag v-if="text === bapl_record.account.pc_online" color="red">Online PC này</a-tag>
                            <a-tag v-else-if="bapl_record.account.pc_online" color="blue">Online</a-tag>
                            <a-tag v-else color="grey">Offline</a-tag>
                        </template>
                        <template v-else-if="column.key === 'is_lock'">
                            <a-button :type="bapl_record.account.is_lock ? 'primary' : 'ghost'" @click="switchLock(bapl_record.account)">
                                <template #icon>
                                    <lock-outlined v-if="!bapl_record.account.is_lock" />
                                    <KeyOutlined v-else />
                                </template>
                            </a-button>
                        </template>
                    </template>
                </a-table>
            </template>
        </a-table>
    </a-card>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";

import { Button, Card, Collapse, Form, Input, message, Select, Table, TablePaginationConfig, Tag, Modal, } from "ant-design-vue";
import {
    SearchOutlined,
    LockOutlined, KeyOutlined,
} from '@ant-design/icons-vue';
import { ColumnType } from "ant-design-vue/lib/table";

import { RouteName } from "@/router";
import ApiRequest from "@/utils/ApiRequest";
import { UserRole } from "@/store/modules/user";
import { DefaultOptionType, } from "ant-design-vue/lib/select";

import type { UserModel } from '@/views/admin/UserListManagerView.vue';

enum LoginCheckType {
    MAC_THEN_HWID = 0,
    MAC_ONLY,
    HWID_ONLY,
    PC,
    BLOCK,
    FREE,
}

const loginCheckTypeText = (type: LoginCheckType) => {
    switch (type) {
        case LoginCheckType.MAC_THEN_HWID:
            return "Cả MAC và HWID (Mặc định)";
        case LoginCheckType.MAC_ONLY:
            return "Chỉ MAC";
        case LoginCheckType.HWID_ONLY:
            return "Chỉ HWID";
        case LoginCheckType.PC:
            return "Chỉ PC (Nên dùng)";
        case LoginCheckType.BLOCK:
            return "Cấm PC đăng nhập";
        case LoginCheckType.FREE:
            return "Đăng nhập thoải mái (Cho GM)";
        default:
            return 'Unknown';
    }
};

export default defineComponent({
    components: {
        ACard: Card,
        ATable: Table,
        AButton: Button,
        AInput: Input,
        AForm: Form,
        AFormItem: Form.Item,
        ACollapse: Collapse,
        ACollapsePanel: Collapse.Panel,
        // AModal: Modal,
        ASelect: Select,
        ATag: Tag,

        SearchOutlined,
        LockOutlined,
        KeyOutlined,
    },

    setup() {
        const { t } = useI18n();

        const optionsCheckType: DefaultOptionType[] = [
            {
                value: LoginCheckType.MAC_THEN_HWID,
                label: loginCheckTypeText(LoginCheckType.MAC_THEN_HWID),
            },
            {
                value: LoginCheckType.MAC_ONLY,
                label: loginCheckTypeText(LoginCheckType.MAC_ONLY),
            },
            {
                value: LoginCheckType.HWID_ONLY,
                label: loginCheckTypeText(LoginCheckType.HWID_ONLY),
            },
            {
                value: LoginCheckType.PC,
                label: loginCheckTypeText(LoginCheckType.PC),
            },
            {
                value: LoginCheckType.BLOCK,
                label: loginCheckTypeText(LoginCheckType.BLOCK),
            },
            {
                value: LoginCheckType.FREE,
                label: loginCheckTypeText(LoginCheckType.FREE),
            },
        ];

        const isLoading = ref(true);
        const dataSource = ref([]);
        const expandedRowKeys = ref<string[]>([]);
        const columns: ColumnType[] = [
            {
                title: 'PC',
                dataIndex: 'pc',
                customFilterDropdown: true,
            },
            {
                title: 'Login Check',
                dataIndex: 'check_type',
                filters: Object.keys(LoginCheckType).filter((v) => isNaN(Number(v))).map((v) => {
                    const role = LoginCheckType[v as keyof typeof LoginCheckType];
                    return ({ text: loginCheckTypeText(role), value: role });
                }),
            },
        ];
        const pagination = ref<TablePaginationConfig>({
            current: 1,
        });

        const formModel = reactive({
            user_name: '',
        });

        const filters = ref({});

        const fetchData = () => {
            isLoading.value = true;

            ApiRequest.get('/bplis', {
                page: pagination.value.current,
                ...filters.value,
            }).then((response) => {
                const resData = response.data;
                dataSource.value = resData.data;

                ApiRequest.setPaginate(pagination.value, resData);
            })
            .finally(() => {
                isLoading.value = false;
            });
        };

        const switchLock = (record: UserModel) => {
            const is_lock = !record.is_lock;
            Modal.confirm({
                title: is_lock ? t('message.userLockConfirm') : t('message.userUnlockConfirm'),
                content: record.name,
                autoFocusButton: 'cancel',
                maskClosable: true,
                onOk: async () => {
                    return ApiRequest.post('/users/' + record.id + '/set-lock', {
                        is_lock: is_lock,
                    }).then(() => {
                        message.success(t('message.updateSuccess'));
                    })
                    .finally(() => {
                        fetchData();
                    });
                },
            });
        };


        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const onChangeCheckType = (record: any, value: number) => {
            isLoading.value = true;

            ApiRequest.patch(`/bplis/${record.id}`, {
                check_type: value,
            }).then(() => {
                message.success(t('message.success'));
            })
            .finally(() => {
                fetchData();
            });
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const onFinish = (values: any) => {
            filters.value = values;
            pagination.value.current = 1;
            fetchData();
        };

        // pagination.value.onChange = (page) => {
        //     fetchData(page);
        // };

        /* eslint-disable @typescript-eslint/no-explicit-any */
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const handleSearch = (selectedKeys: any, confirm: any, dataIndex: any) => {
            confirm();
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const handleReset = (clearFilters: any) => {
            clearFilters({ confirm: true });
        };

        /* eslint-disable @typescript-eslint/no-explicit-any */
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const onChange = (_pagination: TablePaginationConfig, fils: any, _sorter: any) => {
            pagination.value = _pagination;

            filters.value = {
                ...filters.value,
                ...fils,
                check_type: fils.check_type ? fils.check_type.join(',') : undefined,
            };

            fetchData();
        };

        const columnsBAPLs: ColumnType[] = [
            {
                title: t('labelText.user_name'),
                dataIndex: ['account', 'name'],
            },
            {
                title: 'Lần cuối đăng nhập trên PC này',
                dataIndex: 'loggedin_at',
            },
            {
                title: 'Trạng thái Online',
                dataIndex: 'pc_id',
            },
            {
                title: t('labelText.user_is_lock'),
                key: 'is_lock',
            },
        ];

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onMounted(() => {
            fetchData();
        });

        onUnmounted(() => {
            //
        });

        return {
            dataSource,
            columns,
            pagination,
            isLoading,
            expandedRowKeys,

            fetchData,
            handleReset,
            handleSearch,
            onChange,
            switchLock,

            formModel,
            onFinish,

            optionsCheckType,
            onChangeCheckType,

            columnsBAPLs,

            RouteName,

            UserRole,
        };
    },
})
</script>

<style scoped>

</style>
